<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, onUnmounted, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSpaceStore } from '@/stores/useSpaceStore'

import utils from '@/utils.js'
import consts from '@/consts.js'

import Links from '@/components/sidebar/Links.vue'
import Tags from '@/components/sidebar/Tags.vue'
import Removed from '@/components/sidebar/Removed.vue'
import Stats from '@/components/sidebar/Stats.vue'
import Inbox from '@/components/sidebar/Inbox.vue'
import Favorites from '@/components/sidebar/Favorites.vue'
import SpaceHistory from '@/components/sidebar/SpaceHistory.vue'
import Tasks from '@/components/sidebar/Tasks.vue'
import Note from '@/components/sidebar/Note.vue'
import AtUserMentions from '@/components/sidebar/AtUserMentions.vue'
import AtDateMentions from '@/components/sidebar/AtDateMentions.vue'
import SidebarResize from '@/components/SidebarResize.vue'

const globalStore = useGlobalStore()
const userStore = useUserStore()
const spaceStore = useSpaceStore()

const maxIterations = 30
let currentIteration, updatePositionTimer

const dialogElement = ref(null)
const titleElement = ref(null)
let unsubscribes

onMounted(() => {
  window.addEventListener('resize', updateDialogHeight)

  const globalActionUnsubscribe = globalStore.$onAction(
    ({ name, args }) => {
      if (name === 'triggerRemovedIsVisible') {
        toggleSection('removed')
      } else if (name === 'triggerNoteIsVisible') {
        toggleSection('note')
      }
    }
  )
  unsubscribes = () => {
    globalActionUnsubscribe()
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDialogHeight)
  unsubscribes()
})

const props = defineProps({
  visible: Boolean
})

const state = reactive({
  dialogHeight: null,
  tagsIsVisible: false,
  linksIsVisible: false,
  removedIsVisible: false,
  inboxIsVisible: false,
  statsIsVisible: false,
  favoritesIsVisible: false,
  historyIsVisible: false,
  tasksIsVisible: false,
  noteIsVisible: false,
  atUserMentionsIsVisible: false,
  atDateMentionsIsVisible: false
})

watch(() => props.visible, (value, prevValue) => {
  if (value) {
    restoreUserLastSidebarSection()
    updateDialogHeight()
    globalStore.shouldExplicitlyHideFooter = true
  } else {
    globalStore.shouldExplicitlyHideFooter = false
  }
})

watch(() => userStore.sidebarResizeWidth, (value, prevValue) => {
  updateDialogHeight()
})

const clearVisible = () => {
  state.linksIsVisible = false
  state.tagsIsVisible = false
  state.removedIsVisible = false
  state.inboxIsVisible = false
  state.statsIsVisible = false
  state.favoritesIsVisible = false
  state.historyIsVisible = false
  state.tasksIsVisible = false
  state.noteIsVisible = false
  state.atUserMentionsIsVisible = false
  state.atDateMentionsIsVisible = false
}
const updateSubsectionHeight = () => {
  const element = titleElement.value
  const rect = element.getBoundingClientRect()
  const height = rect.height
  state.subsectionHeight = state.dialogHeight - height
}
const updateDialogHeight = async () => {
  if (!props.visible) { return }
  await nextTick()
  const element = dialogElement.value
  state.dialogHeight = utils.elementHeight(element)
  await nextTick()
  updateSubsectionHeight()
}
const updateHeights = () => {
  if (!props.visible) {
    window.cancelAnimationFrame(updatePositionTimer)
    updatePositionTimer = undefined
    return
  }
  currentIteration = 0
  if (updatePositionTimer) { return }
  updatePositionTimer = window.requestAnimationFrame(updateHeightsFrame)
}
const updateHeightsFrame = () => {
  currentIteration++
  updateDialogHeight()
  if (currentIteration < maxIterations) {
    window.requestAnimationFrame(updateHeightsFrame)
  } else {
    window.cancelAnimationFrame(updatePositionTimer)
    updatePositionTimer = undefined
  }
}

// pin dialog

const dialogIsPinned = computed(() => globalStore.sidebarIsPinned)
const toggleDialogIsPinned = () => {
  const isPinned = !dialogIsPinned.value
  globalStore.sidebarIsPinned = isPinned
  setTimeout(() => {
    updateDialogHeight()
  }, 100) // wait for pinned dialog transition
}
const closeDialogs = () => {
  globalStore.tagDetailsIsVisible = false
  globalStore.triggerCloseChildDialogs()
}

// current section

const toggleSection = (value) => {
  clearVisible()
  state[`${value}IsVisible`] = true
  updateUserLastSidebarSection(value)
}

// last sidebar section

const restoreUserLastSidebarSection = () => {
  clearVisible()
  const section = userStore.lastSidebarSection
  const values = ['stats', 'inbox', 'removed', 'links', 'tags', 'favorites', 'history', 'tasks', 'note', 'atUserMentions', 'atDateMentions'] // listed in api docs
  const isValid = values.includes(section)
  if (section && isValid) {
    state[section + 'IsVisible'] = true
  } else {
    state.inboxIsVisible = true
  }
}
const updateUserLastSidebarSection = (name) => {
  userStore.updateUser({ lastSidebarSection: name })
}

// styles

const styles = computed(() => {
  const minWidth = consts.defaultDialogWidth
  let width = userStore.sidebarResizeWidth || minWidth
  width = `${width}px`
  return {
    width,
    maxHeight: state.dialogHeight + 'px'
  }
})

</script>

<template lang="pug">
dialog#sidebar.sidebar.is-pinnable(
  v-if="visible"
  :open="visible"
  @click.left.stop="closeDialogs"
  ref="dialogElement"
  :style="styles"
  :data-is-pinned="dialogIsPinned"
  :class="{'is-pinned': dialogIsPinned}"
)
  section.title-section(ref="titleElement")
    .row.title-row-flex
      .button-wrap.segmented-buttons-wrap
        //- first row
        .segmented-buttons
          //- Inbox
          button(@click.left="toggleSection('inbox')" :class="{ active: state.inboxIsVisible}" title="Move from Inbox")
            img.icon(src="@/assets/inbox.svg")
          //- Favorites
          button(@click.left="toggleSection('favorites')" :class="{ active: state.favoritesIsVisible}" title="Favorites")
            img.icon(src="@/assets/heart-empty.svg")
          //- Stats
          button(@click.left="toggleSection('stats')" :class="{active: state.statsIsVisible}" title="Stats")
            img.icon.stats(src="@/assets/stats.svg")
          //- Tags
          button(@click.left="toggleSection('tags')" :class="{ active: state.tagsIsVisible}" title="Space Tags")
            span Tags
          //- Links
          button(@click.left="toggleSection('links')" :class="{ active: state.linksIsVisible}" title="Backlinks")
            span Links
        //- second row
        .segmented-buttons
          //- Tasks
          button(@click.left="toggleSection('tasks')" :class="{ active: state.tasksIsVisible}" title="Tasks")
            span Tasks
          //- @
          button(@click.left="toggleSection('atUserMentions')" :class="{ active: state.atUserMentionsIsVisible}" title="@User Mentions")
            img.icon.at(src="@/assets/at.svg")
          //- Dates
          button(@click.left="toggleSection('atDateMentions')" :class="{ active: state.atDateMentionsIsVisible}" title="Due Dates")
            img.icon.cal(src="@/assets/cal.svg")
          //- Note
          button(@click.left="toggleSection('note')" :class="{ active: state.noteIsVisible}" title="Note")
            img.icon.note(src="@/assets/note.svg")
          //- Favorites
          button(@click.left="toggleSection('history')" :class="{ active: state.historyIsVisible}" title="Space History")
            img.icon.time(src="@/assets/time.svg")
          //- Removed
          button(@click.left="toggleSection('removed')" :class="{ active: state.removedIsVisible}" title="Removed Cards and Spaces")
            img.icon(src="@/assets/remove.svg")
            img.icon.remove-undo(src="@/assets/undo.svg")

      //- Pin
      .button-wrap.pin-button-wrap(@click.left="toggleDialogIsPinned" title="Pin dialog")
        button.small-button(:class="{active: dialogIsPinned}")
          img.icon.pin.right-pin(src="@/assets/pin.svg")

  Tags(:visible="state.tagsIsVisible" :parentIsPinned="dialogIsPinned" :subsectionHeight="state.subsectionHeight")
  Links(:visible="state.linksIsVisible" :parentIsPinned="dialogIsPinned" :subsectionHeight="state.subsectionHeight")
  Removed(:visible="state.removedIsVisible" :subsectionHeight="state.subsectionHeight")
  Stats(:visible="state.statsIsVisible" :subsectionHeight="state.subsectionHeight")
  Inbox(:visible="state.inboxIsVisible" :subsectionHeight="state.subsectionHeight")
  Favorites(:visible="state.favoritesIsVisible" :subsectionHeight="state.subsectionHeight")
  SpaceHistory(:visible="state.historyIsVisible" :subsectionHeight="state.subsectionHeight")
  Tasks(:visible="state.tasksIsVisible" :subsectionHeight="state.subsectionHeight")
  AtUserMentions(:visible="state.atUserMentionsIsVisible" :subsectionHeight="state.subsectionHeight")
  AtDateMentions(:visible="state.atDateMentionsIsVisible" :subsectionHeight="state.subsectionHeight")
  Note(:visible="state.noteIsVisible" :subsectionHeight="state.subsectionHeight" @updateDialogHeight="updateHeights")

  SidebarResize
</template>

<style lang="stylus">
.sidebar
  top calc(100% - 8px)
  left initial
  right 8px
  max-height calc(100vh - 25px)
  &.is-pinned
    top -36px
    right 0
  .title-row-flex
    align-items flex-start
  .right-pin
    transform rotate(180deg)
  .icon.flower
    vertical-align -1px
    height 11px
  .icon.time,
  .icon.at
    vertical-align -1px
  .icon.cal
    vertical-align -2px

  .segmented-buttons + .segmented-buttons
    margin-left 0

  .pin-button-wrap
    position absolute
    right 0
    top 0

  .segmented-buttons-wrap
    .segmented-buttons
      // &:first-child
      //   button,
      //   label
      //     &:last-child
      //       border-bottom-right-radius 0
      // middle row
      // &:nth-child(2)
      //   margin-top -1px
      //   button,
      //   label
      //     &:first-child
      //       border-top-left-radius 0
      //       border-bottom-left-radius 0
      //     // &:last-child
      //       // border-top-right-radius 0
      // last row
      &:first-child
        button,
        label
          &:last-child
            border-bottom-right-radius 0

      &:last-child
        button,
        label
          &:first-child
            border-top-left-radius 0
          &:last-child
            border-top-right-radius var(--entity-radius)

</style>
